import React from 'react';
import { LockOutlined } from '@ant-design/icons';
import { ActionButton, BackButton, GridContainer } from 'shared/ui';
import { useIntl } from 'react-intl';
import { Card, CStatus, CType } from 'entities/card/model/types';
import classes from './CardsLayout.module.scss';
import { Carousel, Flex, Typography } from 'antd';
import CardIcon from 'shared/ui/CardIcon/CardIcon';
import { FreezeCard } from 'features/card/freeze-card';
import { useSelector } from 'react-redux';
import { getUserMeta } from 'shared/config/store/reducers/userSlice';
import { ActivateCard } from 'features/card/activate-card';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from 'shared/const/routes';
import CardRules from 'widgets/card-rules/ui/CardRules';
import { Reset3dsButton } from 'features/card/reset-3ds';
import { ReplaceCard } from 'features/card/replace-card';
import Requisites from '../Requisites/Requisites';
import { getCurrentEmi } from 'shared/config/store/selectors/app';
import { getEMICurrentAccountObject } from 'shared/config/store/reducers/accountsSlice';
import { RootState } from 'shared/config/store/store';
import { Account } from 'entities/account/model/types';
import { useGetCountriesQuery } from 'entities/countries/api';
import { getFormattedAddress } from 'entities/countries/model/utils';

const { Text } = Typography;

export type Props = {
  card: Card;
  accountId: string | number;
  onStatementDownload?: ({
    dateRange,
    format
  }: {
    dateRange: Array<string>;
    format: 'pdf' | 'csv';
  }) => void | Promise<any>;
};

const CardsLayout: React.FC<Props> = ({ card, accountId }) => {
  const { formatMessage: t } = useIntl();
  const navigate = useNavigate();

  const isVirtual = card.type === CType.Virtual;
  const userMeta = useSelector(getUserMeta);
  const isDispatched = card.status === CStatus.Dispatched;
  const isOrdered = card.status === CStatus.New || card.status === CStatus.Personalized;
  const isBlocked = card.status === CStatus.Blocked;
  const isFrozen = card.status === CStatus.SoftBlocked;
  
  const currentEmi = useSelector(getCurrentEmi);
  const currentAccount = useSelector((state: RootState) =>
    getEMICurrentAccountObject(state, currentEmi)
  ) as Account | undefined;

  const { data: countriesResponse } = useGetCountriesQuery();
  const countries = countriesResponse?.response ?? [];
  const address = getFormattedAddress(userMeta.address, countries);

  const handleBlockClick = () => {
    navigate(ROUTES.BLOCK_CARD.path);
  };

  const renderDispatched = () => {
    return (
      <Flex vertical align='center' gap={16}>
        <Flex vertical align='center'>
          <Text style={{ textAlign: 'center' }}>{t({ id: 'card.getPlastic.label.address' })}</Text>
          <Text style={{ fontWeight: 'bold', textAlign: 'center' }}>
            {address}
          </Text>
        </Flex>
        <ActivateCard />
      </Flex>
    );
  };

  const renderOrdered = () => {
    return (
      <Flex vertical align='center'>
        <Text style={{ textAlign: 'center' }}>{t({ id: 'card.getPlastic.label.delivered' })}</Text>
        <Text style={{ fontWeight: 'bold', textAlign: 'center' }}>
          {address}
        </Text>
      </Flex>
    );
  };

  const renderedBlocked = () => {
    return (
      <Flex vertical align='center'>
        <ReplaceCard />
      </Flex>
    );
  };

  const renderFrozen = () => {
    return (
      <Flex vertical align='center' gap={10}>
        <Flex className='w100' gap={10}>
          <FreezeCard />
          <ActionButton
            text={t({ id: 'card.block.button.short' })}
            icon={<LockOutlined style={{ fontSize: 24 }} />}
            onClick={handleBlockClick}
          />
        </Flex>
        <Requisites
          numberMasked={card.numberMasked}
          accountId={String(accountId)}
          cardId={String(card.id)}
        />
      </Flex>
    );
  };

  const renderDefault = () => {
    return (
      <Flex vertical align='center' gap={10}>
        <Flex className='w100' gap={10}>
          <FreezeCard />
          <ActionButton
            text={t({ id: 'card.block.button.short' })}
            icon={<LockOutlined style={{ fontSize: 24 }} />}
            onClick={handleBlockClick}
          />
          <Reset3dsButton />
        </Flex>
        <Requisites
          numberMasked={card.numberMasked}
          accountId={String(accountId)}
          cardId={String(card.id)}
        />
      </Flex>
    );
  };

  return (
    <GridContainer>
      <div className={classes.header}>
        <BackButton />
        <div className={classes['header_title']}>
          {isVirtual ? t({ id: 'river.virtualCard' }) : t({ id: 'river.plasticCard' })}
        </div>
      </div>
      <div className={classes.center}>
        <Carousel
          dots={{ className: classes.dots }}
          style={{ overflow: 'visible', width: '396px' }}
          initialSlide={0}
          rootClassName={classes.carousel}
          centerMode
          slidesToShow={1}
          draggable
          infinite={false}
          focusOnSelect
          afterChange={() => { }}
        >
          <span key={`${card.id}`}>
            <div className={classes.cards}>
              <CardIcon 
                size='l' 
                type={card.type}
                status={card.status} 
                cardNumber={card.numberMasked}
                accountTitle={currentAccount?.title}
                balance={currentAccount?.available}
              />
            </div>
          </span>
        </Carousel>
      </div>
      <div>
        {isDispatched && renderDispatched()}
        {isOrdered && renderOrdered()}
        {isBlocked && renderedBlocked()}
        {isFrozen && renderFrozen()}
        {!isDispatched && !isOrdered && !isBlocked && !isFrozen && renderDefault()}
      </div>

      {!isBlocked && !isDispatched && !isOrdered && (
        <CardRules
          accountId={String(accountId)}
          cardId={String(card.id)}
          cardType={card.type}
          cardStatus={card.status}
        />
      )}
    </GridContainer>
  );
};

export default CardsLayout;
